import { useState, useEffect } from 'react';
import { Cloud, Sun, Moon, CloudRain, Clock, Bookmark, Search } from 'lucide-react';
import { useTheme } from './ThemeProvider';
import { Link } from 'react-router-dom';

export function WeatherPrayerBar() {
  const { theme, toggleTheme } = useTheme();
  const [temp, setTemp] = useState<number | null>(null);
  const [weatherCode, setWeatherCode] = useState<number | null>(null);
  const [nextPrayer, setNextPrayer] = useState<{name: string, time: string} | null>(null);
  
  useEffect(() => {
    // Fetch Weather (Dhaka)
    fetch('https://api.open-meteo.com/v1/forecast?latitude=23.8103&longitude=90.4125&current=temperature_2m,weather_code')
      .then(res => res.json())
      .then(data => {
        if (data && data.current) {
          setTemp(data.current.temperature_2m);
          setWeatherCode(data.current.weather_code);
        }
      })
      .catch(err => console.error(err));
      
    // Fetch Prayer (Dhaka)
    fetch('https://api.aladhan.com/v1/timingsByCity?city=Dhaka&country=Bangladesh&method=1')
      .then(res => res.json())
      .then(data => {
        if (data && data.data && data.data.timings) {
          const timings = data.data.timings;
          const now = new Date();
          const currentHour = now.getHours();
          const currentMin = now.getMinutes();
          const currentTimeStr = `${currentHour.toString().padStart(2, '0')}:${currentMin.toString().padStart(2, '0')}`;
          
          const prayers = [
            { id: 'Fajr', bn: 'ফজর' },
            { id: 'Dhuhr', bn: 'যোহর' },
            { id: 'Asr', bn: 'আসর' },
            { id: 'Maghrib', bn: 'মাগরিব' },
            { id: 'Isha', bn: 'এশা' }
          ];
          
          let upcoming = prayers[0];
          for (const prayer of prayers) {
            if (timings[prayer.id] > currentTimeStr) {
               upcoming = prayer;
               break;
            }
          }
          
          setNextPrayer({
             name: upcoming.bn,
             time: timings[upcoming.id]
          });
        }
      })
      .catch(err => console.error(err));
  }, []);

  const getWeatherIcon = (code: number) => {
    if (code <= 3) return <Sun size={12} className="inline mr-1 text-orange-500" />;
    if (code <= 69) return <Cloud size={12} className="inline mr-1 text-gray-500" />;
    return <CloudRain size={12} className="inline mr-1 text-blue-500" />;
  };
  
  const toBnNum = (str: string | number) => {
    const bnNums = ['০', '১', '২', '৩', '৪', '৫', '৬', '৭', '৮', '৯'];
    return String(str).replace(/\d/g, d => bnNums[parseInt(d)]);
  };
  
  const formatTime = (time24: string) => {
    const [h, m] = time24.split(':');
    let hNum = parseInt(h);
    const ampm = hNum >= 12 ? 'পিএম' : 'এএম';
    hNum = hNum % 12 || 12;
    return `${toBnNum(hNum)}:${toBnNum(m)} ${ampm}`;
  };

  const today = new Intl.DateTimeFormat('bn-BD', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }).format(new Date());

  return (
    <div className="flex justify-between items-center px-4 lg:px-8 py-0 min-h-[40px] md:min-h-[44px] border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 text-[11px] md:text-xs text-[#666666] font-bengali">
       <div className="flex items-center gap-1.5 min-w-[150px] md:min-w-[200px]">
         <span className={`flex items-center transition-opacity duration-300 ${temp !== null && weatherCode !== null ? 'opacity-100' : 'opacity-0'}`} title="ঢাকা (তাপমাত্রা)">
           {weatherCode !== null ? getWeatherIcon(weatherCode) : <Sun size={12} className="inline mr-1" />}
           <span>{temp !== null ? `${toBnNum(Math.round(temp))}°C` : '৩২°C'}</span>
         </span>
         <span className={`flex items-center ml-2 border-l border-gray-200 dark:border-gray-700 pl-2 transition-opacity duration-300 ${nextPrayer ? 'opacity-100' : 'opacity-0'}`}>
           <Clock size={12} className="inline mr-1 text-green-600 dark:text-green-500" />
           <span title="পরবর্তী ওয়াক্ত">{nextPrayer ? `${nextPrayer.name} ${formatTime(nextPrayer.time)}` : 'আসর ৪:০০ পিএম'}</span>
         </span>
       </div>
       <div className="flex items-center gap-4">
         <div className="hidden md:block">
           {today}
         </div>
         <div className="hidden lg:flex items-center ml-2 border-l border-gray-200 dark:border-gray-700 pl-4 lg:pl-6">
           <Link to="/bookmarks" className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 active:scale-95 transition-transform" aria-label="Bookmarks">
             <Bookmark size={16} />
           </Link>
           <button 
             onClick={toggleTheme}
             className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 active:scale-95 transition-transform" 
             aria-label="Toggle Dark Mode"
           >
             {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
           </button>
           <Link to="/search" className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 active:scale-95 transition-transform" aria-label="Search">
             <Search size={16} />
           </Link>
           <Link to="/shorts" className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 active:scale-95 transition-transform" aria-label="Shorts">
             <span className="font-bold flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span> শর্টস</span>
           </Link>
         </div>
       </div>
    </div>
  );
}
